// eslint-disable-next-line no-restricted-imports
import moment from "moment";
import { addError } from "@wayflyer/blackbox-fe";

import { Dimensions, getMetricOverrideFromMetricType, MetricTypes } from "../autogen/metrics";
import { getMetricData, MetricAggregationType } from "../autogen/aggregation-data";

import type { CustomTimeRange, Metric, TableData, TemporalResolution } from "./types";
import { TemporalResolutions } from "./types";
import fetchDashboardData, { type DataPrefix } from "./fetch-dashboard-data";
import { dateForResolution } from "./custom-time-range-helpers";

export const fetchedData: Record<string, TableData> = {};
const fetchedDataPromises: Record<string, Promise<void>> = {};

const momentCache = {};
const getMoment = (s: string): moment.Moment => {
  if (!momentCache[s]) {
    momentCache[s] = moment(s);
  }
  return momentCache[s];
};

const getFetchKey = (d: DataToFetch): string => {
  return `${d.filename}-${(d.groupBys || []).toString()}`;
};

export type DataPoint = Partial<Record<MetricTypes | "day", string | number>>;

export type DashboardOutput = {
  summary: DataPoint;
  data: DataPoint[];
  groups: string[][];
  metrics: Metric[];
  currencyCode: string;
};

export type DataToFetch = {
  metrics: MetricTypes[];
  filename: DataPrefix | string;
  groupBys?: Dimensions[];
};

const sanitizeDataToFetch = (_dataToFetch: DataToFetch[]): DataToFetch[] => {
  return _dataToFetch.map((d) => {
    return {
      metrics: d.metrics.filter((m) => m),
      filename: d.filename,
      groupBys: d.groupBys ? d.groupBys.filter((g) => g) : [],
    };
  });
};

const getCurrencyCode = (fetchedData: Record<string, TableData>, dataToFetch: DataToFetch[]) => {
  // Note, this could be different for different files if things get a bit racey in generation
  const currencyCode = fetchedData[getFetchKey(dataToFetch[0])].currencyCode;
  if (dataToFetch.some((d) => fetchedData[getFetchKey(d)].currencyCode !== currencyCode)) {
    addError(new Error(`Mismatching currency codes for dashboard files`));
  }
  return currencyCode;
};

function sanitiseAggregatedStrings(aggregated: DataPoint) {
  // Doesn't make much sense to display the 'aggregated' string in the summary line
  // Aggregated here meaning the first non null value we came across
  Object.keys(aggregated).forEach((key) => {
    const metricData = getMetricData(key as MetricTypes);
    if (metricData && metricData.type === MetricAggregationType.String) {
      aggregated[key] = "-";
    }
  });
}

const _generateOutput = (
  dataToFetch: DataToFetch[],
  callback: (res: DashboardOutput) => void,
  timeRange: CustomTimeRange | null,
  aggregateBy?: Dimensions | null,
  temporalResolution?: TemporalResolution
) => {
  let data: DataPoint[];
  let groups: Array<string>[] = [];

  if (!aggregateBy) {
    const rv = groupByDate(dataToFetch, timeRange, temporalResolution ?? TemporalResolutions.DAILY);
    data = rv.data;
    groups = rv.groups;
  } else {
    data = groupByThing(dataToFetch, aggregateBy, timeRange);
  }

  const metricTypes = Array.from(
    getNeedToIncludeMetricTypes(
      dataToFetch.reduce((soFar, data) => soFar.concat(data.metrics), [] as MetricTypes[])
    ).values()
  );
  const aggregated = aggregateDataPoints(data, metricTypes, groups);
  sanitiseAggregatedStrings(aggregated);

  const metrics = getMetrics(dataToFetch);

  const currencyCode = getCurrencyCode(fetchedData, dataToFetch);

  callback({ summary: aggregated, data, groups: groups, metrics, currencyCode });
};

export const getDashboardOutput = (
  dashboardDataPath: string,
  _dataToFetch: DataToFetch[],
  timeRange: CustomTimeRange,
  callback: (res: DashboardOutput) => void,
  aggregateBy?: Dimensions | null,
  onFailure?: (error: string) => void,
  temporalResolution?: TemporalResolution
) => {
  const dataToFetch = sanitizeDataToFetch(_dataToFetch);
  const generateOutput = () => _generateOutput(dataToFetch, callback, timeRange, aggregateBy, temporalResolution);

  waitForData(dashboardDataPath, dataToFetch, generateOutput, onFailure, true);
};

// For cases when time filtering is not relevant
// Reads the dashboard file directly and transforms into DashboardOutput
const waitForData = (
  dashboardDataPath: string,
  dataToFetch: DataToFetch[],
  cb: () => void,
  onFailure?: (error: string) => void,
  groupByDate?: boolean
) => {
  Promise.all(dataToFetch.map((d) => getData(dashboardDataPath, d, groupByDate))).then(
    () => {
      cb();
    },
    (error) => {
      if (onFailure) {
        onFailure(error);
      } else {
        addError(error);
      }
    }
  );
};

const getData = (dashboardDataPath: string, d: DataToFetch, groupByDate?: boolean) => {
  fetchedDataPromises[getFetchKey(d)] =
    fetchedDataPromises[getFetchKey(d)] ||
    new Promise<void>((resolve, reject) => {
      fetchDashboardData(
        dashboardDataPath,
        d.filename,
        "TO_DATE",
        [],
        groupByDate ? ["DAILY"].concat(d.groupBys ?? []) : (d.groupBys ?? []),
        [],
        [],
        (data) => {
          fetchedData[getFetchKey(d)] = data;
          resolve();
        },
        (error) => {
          reject(error);
        }
      );
    });
  return fetchedDataPromises[getFetchKey(d)];
};

const getMetrics = (dataToFetch: DataToFetch[]): Metric[] => {
  const rv: Metric[] = [];
  dataToFetch.forEach((d) => {
    const currencyCode = fetchedData[getFetchKey(d)].currencyCode;
    d.metrics.forEach((m) => {
      // it would be great to be able to type the return value of getMetricOverrideFromMetricType
      // but that's not possible as it's auto-generated
      const metricOverride = getMetricOverrideFromMetricType(m, currencyCode) as Metric;
      if (metricOverride) {
        rv.push(metricOverride);
      } else {
        throw new Error(`${m} not in available metrics`);
      }
    });
  });
  return rv;
};

const getNeedToIncludeMetricTypes = (metrics: MetricTypes[]) => {
  return metrics.reduce((soFar, metric) => {
    const metricData = getMetricData(metric);
    soFar.add(metricData.name);
    if (metricData.type === MetricAggregationType.Ratio) {
      soFar.add(metricData.denominator);
      soFar.add(metricData.numerator);
    }
    return soFar;
  }, new Set<MetricTypes>());
};

const parseCell = (s: string | number | undefined): number => {
  if (!s) {
    return 0;
  }
  if (typeof s === "string") {
    return parseFloat(s) || 0;
  }
  return s;
};

export const aggregateDataPoints = (data: DataPoint[], metrics: MetricTypes[], groups_: string[][]): DataPoint => {
  const groups = groups_.length ? groups_ : [""];

  const safeDivide = (num?: number, denom?: number) => {
    if (!denom) {
      return 0;
    }
    return (num || 0) / denom;
  };
  const averages = {};
  data.forEach((dataPoint) => {
    Object.keys(dataPoint).forEach((key) => {
      const metricData = getMetricData(key as MetricTypes);

      if (metricData && metricData.type === MetricAggregationType.String) {
        averages[key] = averages[key] || dataPoint[key];
      } else {
        averages[key] = (averages[key] || 0) + parseCell(dataPoint[key]);
      }
    });
  });

  const rv = metrics.reduce((rv: any, metric: MetricTypes) => {
    const metricData = getMetricData(metric);
    groups.forEach((group) => {
      const strgrp = group.toString();
      if (metricData.type === MetricAggregationType.Simple) {
        rv[strgrp + metric] = averages[strgrp + metric] || 0;
      } else if (metricData.type === MetricAggregationType.Mean) {
        rv[strgrp + metric] = safeDivide(averages[strgrp + metric], data.length);
      } else if (metricData.type === MetricAggregationType.String) {
        rv[strgrp + metric] = averages[strgrp + metric] || "";
      } else {
        rv[strgrp + metric] = safeDivide(
          averages[strgrp + metricData.numerator],
          averages[strgrp + metricData.denominator]
        );

        // Keep these around in case this an intermediary aggregation, else the summary won't have these to sum
        rv[strgrp + metricData.numerator] = averages[strgrp + metricData.numerator] || 0;
        rv[strgrp + metricData.denominator] = averages[strgrp + metricData.denominator] || 0;
      }
    });
    return rv;
  }, {});

  return rv as DataPoint;
};

const getDataPointFromRow = (
  row: string[],
  tableData: TableData,
  needToInclude: Set<string>,
  prefix = ""
): DataPoint => {
  const rv = {};
  const interestingMetrics = tableData.metrics.filter((metric) => needToInclude.has(metric.name as MetricTypes));

  interestingMetrics.forEach((metric) => {
    const index = tableData.metrics.findIndex((m) => m.name === metric.name);
    rv[prefix + metric.name] = row[tableData.groupByDimensions.length + index];
  });
  return rv;
};

const toList = (rv: { [p: string]: DataPoint }) => {
  const res: DataPoint[] = [];
  Object.keys(rv).forEach((day) => {
    res.push({ day, ...rv[day] });
  });
  res.sort((row1, row2) => (getMoment(row1[0]) > getMoment(row2[0]) ? 1 : 0));
  return res;
};

type GroupByDateRv = {
  data: DataPoint[];
  groups: string[][];
};

const groupByDate = (
  toFetch: DataToFetch[],
  timeRange: CustomTimeRange | null,
  temporalResolution: TemporalResolution
): GroupByDateRv => {
  const rv: { [k: string]: DataPoint } = {};
  const strgrps = new Set<string>();
  const grps = new Array<string[]>();

  toFetch.forEach((data) => {
    const needToInclude = getNeedToIncludeMetricTypes(data.metrics);
    const tableData = fetchedData[getFetchKey(data)];

    const filtered = timeRange
      ? tableData.data.filter((row) => {
          const day = getMoment(row[0]);
          return day >= timeRange.start && day <= timeRange.end;
        })
      : tableData.data;

    const byDate = {};
    filtered.forEach((row) => {
      const day = getMoment(row[0]);
      const timeKey = dateForResolution(day, temporalResolution)?.format("YYYY-MM-DD");

      let prefix = "";
      let group: string[] = [];
      if (data.groupBys && data.groupBys.length > 0) {
        group = row.slice(1, 1 + data.groupBys.length);
        prefix = group.toString();
        if (!strgrps.has(prefix)) {
          strgrps.add(prefix);
          grps.push(group);
        }
      }

      if (timeKey && !(timeKey in byDate)) {
        byDate[timeKey] = [];
      }
      if (timeKey) {
        byDate[timeKey].push(getDataPointFromRow(row, tableData, needToInclude, prefix));
      }
    });

    Object.keys(byDate).forEach((key) => {
      // Aggregate date groups, summing or summing weighted as appropriate
      rv[key] = {
        ...(rv[key] || {}),
        ...aggregateDataPoints(byDate[key], data.metrics, grps),
      };
    });
  });

  return { data: toList(rv), groups: grps };
};

const groupByThing = (toFetch: DataToFetch[], groupBy: Dimensions, timeRange: CustomTimeRange | null): DataPoint[] => {
  const rv: { [k: string]: DataPoint } = {};

  toFetch.forEach((data) => {
    const needToInclude = getNeedToIncludeMetricTypes(data.metrics);
    const tableData = fetchedData[getFetchKey(data)];

    const filtered =
      timeRange == null
        ? tableData.data
        : tableData.data.filter((row) => {
            const day = getMoment(row[0] as string);
            return day >= timeRange.start && day <= timeRange.end;
          });

    const groupIndex = tableData.groupByDimensions.indexOf(groupBy);

    const grouped = filtered.reduce((rv, row) => {
      const groupThing = row[groupIndex];
      // Sometimes we get null groupThings, worth investigating
      if (groupThing) {
        (rv[groupThing] = rv[groupThing] || []).push(row);
      }
      return rv;
    }, {});

    // Aggregate thing groups, summing or summing weighted as appropriate
    const aggData = Object.keys(grouped).reduce((soFar, thing) => {
      soFar[thing] = aggregateDataPoints(
        grouped[thing].map((row) => getDataPointFromRow(row, tableData, needToInclude)),
        data.metrics,
        []
      );
      return soFar;
    }, {});

    // TODO If two files with same metrics, we should aggregate here
    Object.keys(aggData).forEach((key) => {
      rv[key] = { ...(rv[key] || {}), ...aggData[key] };
    });
  });

  const res: DataPoint[] = [];
  Object.keys(rv).forEach((thing) => {
    res.push({ [groupBy]: thing, ...rv[thing] });
  });
  return res;
};
